import { useCallback, useEffect, useState } from 'react';
import { ApiError, get, post } from '../api';
import type { Contexto, Ejecucion, Propuesta } from '../tipos';
import { Avisos, Cifra, Dist, Vacio } from '../components/ui';
import { etiquetaFecha, formatear } from '../components/GraficoPlan';
import { Propuestas } from './Propuestas';
import { Excepciones } from './Excepciones';
import { Articulos } from './Articulos';
import { DetalleArticulo } from './DetalleArticulo';

type Pestana = 'resumen' | 'propuestas' | 'excepciones' | 'articulos';

interface Resumen {
  run: Ejecucion;
  bySeverity: Record<string, number>;
  byAction: Record<string, number>;
  top: Propuesta[];
}

export function Plan({
  contexto,
  sociedad,
  onCambiarSociedad,
}: {
  contexto: Contexto;
  sociedad: string;
  onCambiarSociedad: (companyDb: string) => void;
}): JSX.Element {
  const [ejecuciones, setEjecuciones] = useState<Ejecucion[]>([]);
  const [runId, setRunId] = useState<string | null>(null);
  const [resumen, setResumen] = useState<Resumen | null>(null);
  const [pestana, setPestana] = useState<Pestana>('resumen');
  const [articulo, setArticulo] = useState<{ codigo: string; almacen: string } | null>(null);
  const [planificando, setPlanificando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [horizonte, setHorizonte] = useState(contexto.tenant.planning.horizonDays);
  const [cubo, setCubo] = useState(contexto.tenant.planning.bucket);

  const puedeConfirmar = contexto.session.role !== 'consulta';

  const cargarEjecuciones = useCallback(async (): Promise<void> => {
    const r = await get<{ runs: Ejecucion[] }>(`/runs?company=${encodeURIComponent(sociedad)}`);
    setEjecuciones(r.runs);
    setRunId((actual) => (actual && r.runs.some((x) => x.id === actual) ? actual : (r.runs[0]?.id ?? null)));
  }, [sociedad]);

  useEffect(() => {
    void cargarEjecuciones();
  }, [cargarEjecuciones]);

  useEffect(() => {
    if (!runId) {
      setResumen(null);
      return;
    }
    let vigente = true;
    void get<Resumen>(`/runs/${runId}`)
      .then((r) => vigente && setResumen(r))
      .catch((e: unknown) => vigente && setError(String(e)));
    return () => {
      vigente = false;
    };
  }, [runId]);

  async function planificar(simulacion: boolean): Promise<void> {
    setPlanificando(true);
    setError(null);
    try {
      const r = await post<{ run: Ejecucion }>('/runs', {
        horizonDays: horizonte,
        bucket: cubo,
        companyDb: sociedad,
        simulation: simulacion,
        label: simulacion ? `Simulación ${horizonte} d` : null,
      });
      await cargarEjecuciones();
      setRunId(r.run.id);
      setPestana('resumen');
      setArticulo(null);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message}${e.hint ? ` — ${e.hint}` : ''}` : String(e));
    } finally {
      setPlanificando(false);
    }
  }

  const run = resumen?.run;

  return (
    <div>
      {/* ── Lanzar ─────────────────────────────────────────────────────── */}
      <div className="tarjeta" style={{ marginBottom: 14, display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <label className="campo">
          Sociedad
          <select value={sociedad} onChange={(e) => onCambiarSociedad(e.target.value)}>
            {contexto.companies.map((c) => (
              <option key={c.companyDb} value={c.companyDb}>
                {c.companyDb} — {c.name}
                {c.cif ? ` (${c.cif})` : ' (sin CIF)'}
              </option>
            ))}
          </select>
        </label>
        <label className="campo">
          Horizonte (días)
          <input type="number" min={1} max={540} value={horizonte} onChange={(e) => setHorizonte(Number(e.target.value))} style={{ width: 90 }} />
        </label>
        <label className="campo">
          Periodo
          <select value={cubo} onChange={(e) => setCubo(e.target.value as typeof cubo)}>
            <option value="day">Diario</option>
            <option value="week">Semanal</option>
            <option value="month">Mensual</option>
          </select>
        </label>
        <button className="primario" disabled={planificando || !puedeConfirmar} onClick={() => void planificar(false)}>
          {planificando ? 'Planificando…' : 'Planificar'}
        </button>
        <button
          className="secundario"
          disabled={planificando || !puedeConfirmar}
          onClick={() => void planificar(true)}
          title="Calcula el plan sin sustituir al vigente. Sus propuestas no se pueden confirmar en SAP"
        >
          Simular
        </button>

        <label className="campo" style={{ marginLeft: 'auto' }}>
          Plan
          <select value={runId ?? ''} onChange={(e) => { setRunId(e.target.value); setArticulo(null); }}>
            {ejecuciones.map((r) => (
              <option key={r.id} value={r.id}>
                {new Date(r.started_at).toLocaleString('es-ES')}
                {r.simulation ? ' · simulación' : ''}
                {r.status === 'failed' ? ' · falló' : ''}
              </option>
            ))}
          </select>
        </label>
      </div>

      {error ? <Avisos titulo="No se ha podido planificar" items={[error]} error /> : null}
      {run?.status === 'failed' && run.error ? <Avisos titulo="Esta planificación falló" items={[run.error]} error /> : null}
      {run?.cif_missing ? (
        <Avisos
          titulo="Este plan se calculó con la sociedad sin CIF"
          items={[
            'Las peticiones a SAP salieron sin la cabecera X-Ecinsa-CIF. El plan es válido, pero el tráfico que lo produjo no lleva identificada la sociedad. Se arregla en la ficha de la sociedad en SAP.',
          ]}
        />
      ) : null}
      {run?.simulation ? (
        <Avisos titulo="Esto es una simulación" items={['No sustituye al plan vigente y sus propuestas no se pueden confirmar en SAP.']} />
      ) : null}

      {!run ? (
        <div className="tarjeta"><Vacio>Todavía no hay ningún plan para esta sociedad. Pulsa «Planificar».</Vacio></div>
      ) : articulo ? (
        <DetalleArticulo runId={run.id} codigo={articulo.codigo} almacen={articulo.almacen} onCerrar={() => setArticulo(null)} />
      ) : (
        <>
          <div className="nav" style={{ marginBottom: 14 }}>
            {(['resumen', 'propuestas', 'excepciones', 'articulos'] as Pestana[]).map((p) => (
              <button key={p} aria-current={pestana === p ? 'page' : undefined} onClick={() => setPestana(p)}>
                {p === 'resumen' ? 'Resumen' : p === 'propuestas' ? 'Propuestas' : p === 'excepciones' ? 'Incidencias' : 'Artículos'}
                {p === 'propuestas' && run.counts.recommendations ? ` (${run.counts.recommendations})` : ''}
                {p === 'excepciones' && run.counts.exceptions ? ` (${run.counts.exceptions})` : ''}
              </button>
            ))}
          </div>

          {pestana === 'resumen' && resumen ? (
            <ResumenPlan resumen={resumen} onVerArticulo={(c, a) => setArticulo({ codigo: c, almacen: a })} onVerTodas={() => setPestana('propuestas')} />
          ) : null}
          {pestana === 'propuestas' ? (
            <Propuestas
              runId={run.id}
              puedeConfirmar={puedeConfirmar}
              esSimulacion={run.simulation}
              onVerArticulo={(c, a) => setArticulo({ codigo: c, almacen: a })}
            />
          ) : null}
          {pestana === 'excepciones' ? <Excepciones runId={run.id} onVerArticulo={(c, a) => setArticulo({ codigo: c, almacen: a })} /> : null}
          {pestana === 'articulos' ? <Articulos runId={run.id} onVerArticulo={(c, a) => setArticulo({ codigo: c, almacen: a })} /> : null}
        </>
      )}
    </div>
  );
}

function ResumenPlan({
  resumen,
  onVerArticulo,
  onVerTodas,
}: {
  resumen: Resumen;
  onVerArticulo: (codigo: string, almacen: string) => void;
  onVerTodas: () => void;
}): JSX.Element {
  const { run, bySeverity, byAction, top } = resumen;
  const tarde = run.counts.late ?? 0;
  const bloqueos = bySeverity.blocker ?? 0;

  return (
    <div>
      {/*
        Las cifras de arriba son un titular, no un gráfico: son números sueltos
        sin evolución que enseñar, y una tarta de cinco porciones diría lo
        mismo peor y ocupando cuatro veces más.
      */}
      <div className="rejilla cifras" style={{ marginBottom: 14 }}>
        <Cifra etiqueta="Propuestas" valor={formatear(run.counts.recommendations ?? 0)} pie={`sobre ${formatear(run.counts.items ?? 0)} artículos`} />
        <Cifra etiqueta="Llegan tarde" valor={formatear(tarde)} tono={tarde > 0 ? 'mal' : undefined} pie={tarde > 0 ? 'no llegan ni lanzándolas hoy' : 'todo llega a tiempo'} />
        <Cifra etiqueta="Incidencias que bloquean" valor={formatear(bloqueos)} tono={bloqueos > 0 ? 'mal' : undefined} pie={bloqueos > 0 ? 'el plan está incompleto ahí' : 'el plan se calculó entero'} />
        <Cifra etiqueta="Comprar" valor={formatear(byAction.purchase ?? 0)} />
        <Cifra etiqueta="Fabricar" valor={formatear(byAction.produce ?? 0)} />
        <Cifra etiqueta="Mover fechas" valor={formatear((byAction.expedite ?? 0) + (byAction.defer ?? 0))} pie="pedidos que ya existen" />
      </div>

      <Avisos titulo="Avisos de esta planificación" items={run.warnings ?? []} />

      <div className="tarjeta" style={{ padding: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', padding: '14px 16px 8px', gap: 10 }}>
          <h2>Lo más urgente</h2>
          <button className="secundario" style={{ marginLeft: 'auto' }} onClick={onVerTodas}>
            Ver todas las propuestas
          </button>
        </div>
        {top.length === 0 ? (
          <Vacio>Ninguna propuesta: con el stock y los pedidos actuales, no hace falta hacer nada.</Vacio>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Artículo</th>
                <th>Acción</th>
                <th className="num">Cantidad</th>
                <th>Lanzar</th>
                <th>Retraso</th>
              </tr>
            </thead>
            <tbody>
              {top.map((r) => (
                <tr key={r.id}>
                  <td>
                    <button
                      onClick={() => onVerArticulo(r.item_code, r.warehouse)}
                      style={{ background: 'none', border: 0, padding: 0, font: 'inherit', color: 'var(--serie-1)', cursor: 'pointer', textAlign: 'left' }}
                    >
                      <strong>{r.item_code}</strong>
                    </button>
                    <div className="sutil">{r.item_name}</div>
                  </td>
                  <td>{r.action === 'purchase' ? 'Comprar' : r.action === 'produce' ? 'Fabricar' : r.action === 'expedite' ? 'Adelantar' : 'Retrasar'}</td>
                  <td className="num mono">{formatear(r.quantity)}</td>
                  <td className="mono">{etiquetaFecha(r.release_date)}</td>
                  <td>{r.late_days > 0 ? <Dist tono="critico" icono="■">{r.late_days} d</Dist> : <span className="sutil">a tiempo</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
